<script setup lang="ts">
/**
 * Rich-text editor for HTML that stays HTML (I-2799): e-mails, signatures,
 * BDMS correspondence. The text is edited in place in a sandboxed frame — no
 * scripts, its own CSS kept inside — so a mail looks like itself, Outlook
 * quote and layout tables included. The toolbar is `NxRichTextToolbar`, the
 * one Write's canvas uses for `.writez`, so both backends look and work the
 * same. `getHTML` returns the stored bytes unchanged until the text is edited.
 *
 * Same field contract as Write's `WriteHtmlEditor`, so `NxEditorInput` can
 * carry either: `getHTML`/`setHTML`/`insertHTML`/`appendHTML`/`prepareSave`/
 * `focus`/`inputElement`, `update:modelValue` on every change, `focusout`
 * once the focus leaves the editor, `save` on Ctrl/Cmd+S.
 */
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { NxButton } from '../Button';
import { NxFindBar } from '../FindBar';
import { NxSlider, NxTextInput } from '../Inputs';
import { NxModal } from '../Modal';
import {
  NxRichTextToolbar,
  richTextExtensionForKey,
  useRichTextExtensions,
  useRichTextVariables,
} from '../RichTextToolbar';
import type {
  NxRichTextCommand,
  NxRichTextCommandId,
  NxRichTextExtension,
  NxRichTextExtensionContext,
  NxRichTextExtraAction,
  NxRichTextVariables,
  NxRichTextPasteMode,
  NxRichTextToolbarGroup,
  NxRichTextToolbarLayout,
  NxRichTextToolbarState,
} from '../RichTextToolbar';
import { i18nInjectionKey } from '../internal';
import {
  applyFontSize,
  applyLineSpacing,
  applyLink,
  caretIntoFirstCell,
  closestIn,
  wrapLooseInline,
  exec,
  insertHtmlAtCaret,
  leaveTabSpan,
  moveToCell,
  placeCaretAtEnd,
  placeCaretAtTextEnd,
  quote,
  removeLink,
  setImageWrap,
  tableEdit,
  tableHtml,
} from './commands';
import { EditHistory, restoreSnapshot, takeSnapshot } from './history';
import {
  BASE_STYLE_ATTR,
  SELECTED_IMAGE_ATTR,
  doctypeOf,
  isHtmlDocument,
  isOfficeHtml,
  matchDestination,
  parseForEditing,
  sanitizeHtml,
  serializeForStorage,
  textOf,
  textToHtml,
} from './htmlDocument';
import { readState, type NxHtmlEditorFontFamily } from './selectionState';

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    label?: string;
    disabled?: boolean;
    /** Toolbar and status line; off for the one-line field of `NxEditorInput`. */
    showActionBar?: boolean;
    focusOnLoad?: boolean;
    /** Scroll inside the editor; off when the host owns the height and the scrolling. */
    internalScroll?: boolean;
    allowImages?: boolean;
    /** Local-backup state of the host (`WriteFieldInput`), shown in the status line. */
    recoveryStatus?: string;
    placeholder?: string;
    spellcheck?: boolean;
    lang?: string;
    /** The toolbar's layout: one compact row (default) or every tool in two rows. */
    toolbarLayout?: NxRichTextToolbarLayout;
    /** Which groups the compact row shows; the rest is under „⋯“. */
    toolbarGroups?: Partial<Record<NxRichTextToolbarGroup, boolean>>;
    /** The host's own commands under „⋯“; picking one emits `extra`. */
    extraActions?: readonly NxRichTextExtraAction[];
    /** Offer „Customise toolbar…“ under „⋯“; picking it emits `customize`. */
    customizable?: boolean;
    /** How pasting from Word starts out; the user can change it under „⋯“. */
    pasteMode?: NxRichTextPasteMode;
    /** Extensions of this editor on top of the provided ones (`provideRichTextExtensions`). */
    extensions?: readonly NxRichTextExtension[];
    /** Placeholder values for the extensions, on top of the provided ones. */
    variables?: NxRichTextVariables;
    fontFamilies?: readonly NxHtmlEditorFontFamily[];
    fontSizes?: readonly number[];
    lineSpacings?: readonly number[];
    /** Largest picture taken in, in bytes. */
    maxImageBytes?: number;
    /**
     * Tab outside a table cell and a list inserts a tab character instead of moving the focus
     * (BLP/nx#3328). Shift+Tab still leaves the text, so the keyboard keeps a way out; the
     * editor says so (`aria-description`, status line). For the full editor of a field — the
     * inline field and the popover keep Tab as the focus move.
     */
    insertTabCharacter?: boolean;
  }>(),
  {
    modelValue: undefined,
    label: 'Text',
    showActionBar: true,
    internalScroll: true,
    allowImages: true,
    recoveryStatus: undefined,
    placeholder: '',
    spellcheck: true,
    lang: undefined,
    toolbarLayout: 'compact',
    toolbarGroups: () => ({}),
    extraActions: () => [],
    customizable: false,
    pasteMode: 'ask',
    fontFamilies: undefined,
    fontSizes: () => [6.75, 7.5, 8, 8.25, 9, 10, 10.5, 11, 12, 13.5, 14, 16, 18, 24, 27, 36, 48],
    lineSpacings: () => [100, 115, 150, 200],
    maxImageBytes: 8 * 1024 * 1024,
    insertTabCharacter: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [html: string];
  focusout: [];
  save: [];
  /** Files dropped or pasted that are not pictures for the text — attachments, for a mail. */
  files: [files: File[]];
  /** One of `extraActions` was picked. */
  extra: [id: string];
  customize: [];
}>();

const i18n = inject(i18nInjectionKey, null);
function tr(key: string, fallback: string, params?: Record<string, unknown>): string {
  const full = `common.rich_text.${key}`;
  const text = i18n?.t(full, params);
  return text && text !== full ? text : fallback;
}

/** Tab no longer moves the focus: the way out (WCAG 2.1.2) is told, not left to be found. */
const tabHint = computed(() =>
  tr(
    'tab_character_hint',
    'Tab inserts a tab character. Outside lists and tables, Shift+Tab leaves the text field.'
  )
);
/** Tab inserts a character only in an editable text. */
const tabCharacter = computed(() => props.insertTabCharacter && !props.disabled);

const families = computed<readonly NxHtmlEditorFontFamily[]>(
  () =>
    props.fontFamilies ?? [
      {
        value: 'sans',
        label: tr('font_sans', 'Sans'),
        stack: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      },
      {
        value: 'serif',
        label: tr('font_serif', 'Serif'),
        stack: "Georgia, 'Times New Roman', Times, serif",
      },
      {
        value: 'mono',
        label: tr('font_mono', 'Mono'),
        stack: "ui-monospace, 'SFMono-Regular', 'Courier New', monospace",
      },
      { value: 'arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
      { value: 'calibri', label: 'Calibri', stack: 'Calibri, Carlito, sans-serif' },
      { value: 'courierNew', label: 'Courier New', stack: "'Courier New', monospace" },
      { value: 'georgia', label: 'Georgia', stack: 'Georgia, serif' },
      { value: 'helvetica', label: 'Helvetica', stack: "'Helvetica Neue', Helvetica, sans-serif" },
      { value: 'segoe', label: 'Segoe UI', stack: "'Segoe UI', sans-serif" },
      { value: 'tahoma', label: 'Tahoma', stack: 'Tahoma, sans-serif' },
      {
        value: 'timesNewRoman',
        label: 'Times New Roman',
        stack: "'Times New Roman', Times, serif",
      },
      { value: 'trebuchet', label: 'Trebuchet MS', stack: "'Trebuchet MS', sans-serif" },
      { value: 'verdana', label: 'Verdana', stack: 'Verdana, sans-serif' },
    ]
);

/** What the HTML backend cannot do; the toolbar shows these greyed out. */
const DISABLED: readonly NxRichTextCommandId[] = ['pdf', 'tableMerge', 'tableSplit'];
const disabledCommands = computed<readonly NxRichTextCommandId[]>(() => [
  ...baseDisabledCommands.value,
  ...(tableHasSpans.value ? (['tableRow', 'tableColumn'] as const) : []),
]);
const baseDisabledCommands = computed<readonly NxRichTextCommandId[]>(() =>
  props.allowImages ? DISABLED : [...DISABLED, 'image']
);

const SHELL = '<!doctype html><html><head></head><body></body></html>';

const root = ref<HTMLElement | null>(null);
const frame = ref<HTMLIFrameElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const toolbar = ref<InstanceType<typeof NxRichTextToolbar> | null>(null);
const extensions = useRichTextExtensions('html', () => props.extensions);
const variables = useRichTextVariables(() => props.variables);

/** What an extension's panel gets: the selection now, and an insert at the caret. */
function extensionContext(): Omit<NxRichTextExtensionContext, 'close'> {
  return {
    format: 'html',
    selectedText: doc?.getSelection()?.toString() ?? '',
    variables: variables.value,
    insert: insertHTML,
  };
}
const ready = ref(false);
const words = ref(0);
const error = ref('');
const pasteMode = ref<NxRichTextPasteMode>(props.pasteMode);
watch(
  () => props.pasteMode,
  (mode) => (pasteMode.value = mode)
);
const pasteOpen = ref(false);
const linkOpen = ref(false);
const tableOpen = ref(false);
const linkHref = ref('');
const selectedImage = shallowRef<HTMLImageElement | null>(null);
const history = new EditHistory();
const historyTick = ref(0);
const state = shallowRef<NxRichTextToolbarState>({
  marks: {},
  heading: 0,
  fontFamily: 'sans',
  fontSize: 11,
  color: '#000000',
  highlight: '#FFFF00',
  canUndo: false,
  canRedo: false,
  hasSelection: false,
  inTable: false,
  pasteMode: 'ask',
});

let doc: Document | null = null;
/** The stored HTML as it came in; returned unchanged until the text is edited. */
let original = '';
/** The body's markup right after loading — the text counts as edited once it differs. */
let loadedBody = '';
let wholeDocument = false;
/** Attributes the stored `<body>` came with; the editor's own are not saved. */
let bodyAttributes: ReadonlySet<string> = new Set();
/** Whether the focus is somewhere in the editor (frame or toolbar). */
let focusInside = false;
let doctype: string | undefined;
let published: string | undefined;
let inCommand = false;
let pendingPaste: { html: string; text: string } | undefined;
/** Pictures still being read in; a save waits for all of them. */
const pendingImages = new Set<Promise<void>>();
/** Counts the texts the frame has shown: work started for one never lands in the next. */
let generation = 0;
/** The file picker opens only from the image command, never from a label around the field. */
let choosingFile = false;
/** The live editable body, for hosts that focus the field through `inputElement`. */
const bodyEl = shallowRef<HTMLElement | null>(null);
/** The caret's table has merged cells: row and column commands would hit the wrong cells. */
const tableHasSpans = ref(false);
let resizeObserver: ResizeObserver | undefined;
let lookObserver: MutationObserver | undefined;
let schemeQuery: MediaQueryList | undefined;
let stateFrame: ReturnType<typeof setTimeout> | undefined;

const body = () => doc?.body ?? null;

/** The body markup without the editor's own marks (a selected picture). */
function bodyHtml(): string {
  const el = body();
  if (!el) return '';
  const image = selectedImage.value;
  image?.removeAttribute(SELECTED_IMAGE_ATTR);
  const html = el.innerHTML;
  image?.setAttribute(SELECTED_IMAGE_ATTR, '');
  return html;
}

function edited(): boolean {
  return !!doc && ready.value && bodyHtml() !== loadedBody;
}

function getHTML(): string {
  if (!doc || !edited()) return original;
  return serializeForStorage(doc, wholeDocument, doctype, bodyAttributes);
}

/**
 * Hands the text to the host at once, on every change: hosts decide on it
 * whether the user has edited (a template or signature that arrives later
 * must not overwrite what was just typed).
 */
function publish(): void {
  if (props.disabled) return;
  const html = getHTML();
  if (html === published) return;
  published = html;
  emit('update:modelValue', html);
}

function configureBody(): void {
  const el = body();
  if (!el) return;
  el.contentEditable = props.disabled ? 'false' : 'true';
  el.spellcheck = props.spellcheck;
  if (props.lang) el.lang = props.lang;
  el.toggleAttribute('data-nx-html-editor-flow', !props.internalScroll);
  if (!props.internalScroll) {
    matchField();
    // The host may still be mid-patch (for example `data-disabled` not yet removed).
    void nextTick(matchField);
  }
  el.setAttribute('aria-label', props.label);
  if (tabCharacter.value) el.setAttribute('aria-description', tabHint.value);
  else el.removeAttribute('aria-description');
  el.setAttribute('role', 'textbox');
  el.setAttribute('aria-multiline', 'true');
  markEmpty();
}

/**
 * A field's frame cannot inherit the page's CSS: its font and colour go in as
 * variables, in a style element the save strips like the base style.
 */
function matchField(): void {
  if (!doc?.head || !root.value) return;
  const view = root.value.ownerDocument.defaultView;
  const style = view?.getComputedStyle(root.value);
  if (!style) return;
  let el = doc.head.querySelector<HTMLStyleElement>(`style[${BASE_STYLE_ATTR}="field"]`);
  if (!el) {
    el = doc.createElement('style');
    el.setAttribute(BASE_STYLE_ATTR, 'field');
    doc.head.append(el);
  }
  el.textContent = `:root {
  --nx-html-editor-field-font: ${style.fontFamily};
  --nx-html-editor-field-size: ${style.fontSize};
  --nx-html-editor-field-leading: ${style.lineHeight};
  --nx-html-editor-field-ink: ${style.color};
}`;
}

/**
 * A flowing field follows the page's look: a theme or design switch changes the
 * field's font and colour, which the frame only gets through `matchField`.
 */
function followFieldLook(): void {
  if (props.internalScroll || !doc) return;
  matchField();
  resize();
}

function markEmpty(): void {
  const el = body();
  if (!el) return;
  const empty = !textOf(el).trim() && !el.querySelector('img, table, hr');
  if (empty && props.placeholder) el.setAttribute('data-nx-html-editor-empty', props.placeholder);
  else el.removeAttribute('data-nx-html-editor-empty');
}

function countWords(): void {
  words.value = textOf(body()).trim().split(/\s+/u).filter(Boolean).length;
}

function resize(): void {
  if (props.internalScroll || !frame.value || !doc) return;
  // The body's own height: the document's is at least the frame's, so a
  // field measured by it could grow but never shrink again.
  const style = doc.defaultView?.getComputedStyle(doc.body);
  const margins =
    (parseFloat(style?.marginTop ?? '') || 0) + (parseFloat(style?.marginBottom ?? '') || 0);
  const height = Math.max(doc.body.scrollHeight, doc.body.getBoundingClientRect().height) + margins;
  // Content the frame cannot wrap (a fixed-width layout) gives the frame a horizontal bar,
  // and that bar takes height from the text: without it the frame would scroll next to the field.
  // The bar is the frame's height minus the document's own, so the frame never grows by it twice.
  const view = doc.defaultView;
  const html = doc.documentElement;
  const bar = view && html.clientHeight > 0 ? Math.max(0, view.innerHeight - html.clientHeight) : 0;
  frame.value.style.height = `${Math.ceil(height + bar)}px`;
}

function refreshState(): void {
  if (!doc || !ready.value) return;
  void historyTick.value;
  state.value = readState(doc, {
    families: families.value,
    sizes: props.fontSizes,
    lineSpacings: props.lineSpacings,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    image: selectedImage.value,
    pasteMode: pasteMode.value,
  });
  const table = closestIn(doc, 'table');
  tableHasSpans.value = !!table?.querySelector(
    'td[colspan]:not([colspan="1"]), th[colspan]:not([colspan="1"]), td[rowspan]:not([rowspan="1"]), th[rowspan]:not([rowspan="1"])'
  );
}

function scheduleState(): void {
  if (!doc || stateFrame) return;
  // One read per frame while the selection moves; a timer where the frame has no animation frames (jsdom).
  stateFrame = setTimeout(() => {
    stateFrame = undefined;
    refreshState();
  }, 16);
}

/** Puts `html` into the frame as the new, unedited text. */
function mount(html: string): void {
  if (!doc) return;
  selectImage(null);
  const parsed = parseForEditing(html);
  doc.replaceChild(doc.importNode(parsed.documentElement, true), doc.documentElement);
  bodyAttributes = new Set(doc.body.getAttributeNames());
  generation++;
  wrapLooseInline(doc.body);
  // Typing into an empty field starts a paragraph, and Enter makes the next
  // one, as in Write — not bare text in <body> or Chrome's <div>s. An
  // untouched empty field still hands back what it was given.
  if (!doc.body.childNodes.length || !doc.body.innerHTML.trim()) doc.body.innerHTML = '<p><br></p>';
  try {
    doc.execCommand('defaultParagraphSeparator', false, 'p');
  } catch {
    // A frame without editing commands (jsdom) keeps its default.
  }
  configureBody();
  bodyEl.value = doc.body;
  loadedBody = bodyHtml();
  history.reset(takeSnapshot(doc.body, loadedBody));
  historyTick.value++;
  countWords();
  resize();
  refreshState();
  resizeObserver?.disconnect();
  // The frame's own constructor: an observer of the page does not see the frame's layout.
  const Observer = (doc.defaultView as (Window & typeof globalThis) | null)?.ResizeObserver;
  if (!props.internalScroll && Observer) {
    resizeObserver = new Observer(resize);
    resizeObserver.observe(doc.documentElement);
  }
}

function setHTML(html: string): void {
  if (html === getHTML() && (ready.value || html === original)) return;
  original = html;
  published = html;
  wholeDocument = isHtmlDocument(html);
  doctype = doctypeOf(html);
  error.value = '';
  if (ready.value) mount(html);
}

watch(
  () => props.modelValue,
  (html) => {
    if (html !== undefined) setHTML(html);
  },
  { immediate: true }
);

watch(
  () =>
    [
      props.disabled,
      props.spellcheck,
      props.lang,
      props.placeholder,
      props.label,
      tabCharacter.value,
      tabHint.value,
    ] as const,
  configureBody
);

/** After every change of the text: history, counters, toolbar, model. */
function changed(typing = false): void {
  if (!doc || !ready.value) return;
  history.record(takeSnapshot(doc.body, bodyHtml()), typing);
  historyTick.value++;
  markEmpty();
  countWords();
  resize();
  refreshState();
  refreshFind();
  publish();
}

function focusFrame(): void {
  if (!doc || props.disabled) return;
  // Focusing the body would put the caret at its start; the selection the
  // user made before clicking the toolbar is put back.
  const selection = doc.getSelection();
  const kept =
    selection?.rangeCount && doc.body.contains(selection.anchorNode)
      ? selection.getRangeAt(0).cloneRange()
      : undefined;
  doc.defaultView?.focus();
  doc.body.focus({ preventScroll: true });
  if (kept && selection) {
    selection.removeAllRanges();
    selection.addRange(kept);
  }
}

/** Runs an edit as one undo step and publishes it. */
function edit(run: () => void): void {
  if (!doc || props.disabled) return;
  focusFrame();
  // Moving the caret records nothing: the undo step this edit pushes would
  // restore the caret of the last input.
  history.noteSelection(takeSnapshot(doc.body, bodyHtml()));
  inCommand = true;
  try {
    run();
  } finally {
    inCommand = false;
  }
  changed();
}

function undo(): void {
  if (!doc) return;
  const snapshot = history.undo(takeSnapshot(doc.body, bodyHtml()));
  if (snapshot) applySnapshot(snapshot);
}

function redo(): void {
  if (!doc) return;
  const snapshot = history.redo(takeSnapshot(doc.body, bodyHtml()));
  if (snapshot) applySnapshot(snapshot);
}

function applySnapshot(snapshot: Parameters<typeof restoreSnapshot>[1]): void {
  if (!doc) return;
  selectImage(null);
  focusFrame();
  restoreSnapshot(doc.body, snapshot);
  historyTick.value++;
  markEmpty();
  countWords();
  resize();
  refreshState();
  refreshFind();
  publish();
}

function selectImage(image: HTMLImageElement | null): void {
  selectedImage.value?.removeAttribute(SELECTED_IMAGE_ATTR);
  selectedImage.value = image;
  image?.setAttribute(SELECTED_IMAGE_ATTR, '');
  refreshState();
}

const familyStack = (value: string) =>
  families.value.find((family) => family.value === value)?.stack ?? value;

function stepFontSize(direction: 1 | -1): void {
  const sizes = props.fontSizes;
  const current = state.value.fontSize;
  const next =
    direction > 0
      ? sizes.find((size) => size > current)
      : [...sizes].reverse().find((size) => size < current);
  if (next) edit(() => applyFontSize(doc!, next));
}

function onCommand(command: NxRichTextCommand): void {
  if (!doc || props.disabled) return;
  const d = doc;
  switch (command.id) {
    case 'undo':
      return undo();
    case 'redo':
      return redo();
    case 'copy':
    case 'cut':
      return void copySelection(command.id === 'cut');
    case 'paste':
      return void pasteFromClipboard();
    case 'mark': {
      const native = {
        bold: 'bold',
        italic: 'italic',
        underline: 'underline',
        strike: 'strikeThrough',
        sup: 'superscript',
        sub: 'subscript',
      } as const;
      return edit(() => exec(d, native[command.mark]));
    }
    case 'clearFormatting':
      return edit(() => exec(d, 'removeFormat'));
    case 'align': {
      const native = {
        left: 'justifyLeft',
        center: 'justifyCenter',
        right: 'justifyRight',
        justify: 'justifyFull',
      } as const;
      return edit(() => exec(d, native[command.align]));
    }
    case 'list':
      return edit(() =>
        exec(d, command.list === 'orderedList' ? 'insertOrderedList' : 'insertUnorderedList')
      );
    case 'indent':
      return edit(() => exec(d, command.delta > 0 ? 'indent' : 'outdent', undefined, true));
    case 'heading':
      return edit(() => exec(d, 'formatBlock', command.level ? `<h${command.level}>` : '<p>'));
    case 'fontFamily':
      return edit(() => exec(d, 'fontName', familyStack(command.family), true));
    case 'fontSize':
      return edit(() => applyFontSize(d, command.size));
    case 'color':
      return edit(() => exec(d, 'foreColor', command.color, true));
    case 'highlight':
      return edit(() => exec(d, 'hiliteColor', command.color, true));
    case 'lineSpacing':
      return edit(() => applyLineSpacing(d, command.spacing));
    case 'link':
      return edit(() => applyLink(d, command.href));
    case 'unlink':
      return edit(() => removeLink(d));
    case 'table':
      return edit(() =>
        caretIntoFirstCell(d, insertHtmlAtCaret(d, tableHtml(command.rows, command.columns)))
      );
    case 'image':
      return chooseImages();
    case 'quote':
      return edit(() => quote(d, command.delta));
    case 'divider':
      return edit(() => exec(d, 'insertHorizontalRule'));
    case 'find':
    case 'replace':
      return openFind(command.id === 'replace');
    case 'pasteMode':
      pasteMode.value = command.mode;
      refreshState();
      return focusFrame();
    case 'tableRow':
      return edit(() => tableEdit(d, 'row', command.op));
    case 'tableColumn':
      return edit(() => tableEdit(d, 'column', command.op));
    case 'imageWrap':
      if (selectedImage.value) {
        const image = selectedImage.value;
        edit(() => setImageWrap(image, command.wrap));
      }
      return;
    case 'imageEdit':
      return openImageDialog();
    case 'imageDelete':
      if (selectedImage.value) {
        const image = selectedImage.value;
        selectImage(null);
        edit(() => image.remove());
      }
      return;
    default:
      return;
  }
}

/* Picture size and description ------------------------------------------ */

const imageDialogOpen = ref(false);
const imageWidth = ref(100);
const imageAlt = ref('');

/** Width in percent of the text column; the size a mail client keeps. */
function widthPercentOf(image: HTMLImageElement): number {
  if (image.style.width.endsWith('%')) return Math.round(parseFloat(image.style.width));
  const column = body()?.clientWidth || image.naturalWidth || 1;
  return Math.round((image.getBoundingClientRect().width / column) * 100);
}

function openImageDialog(): void {
  const image = selectedImage.value;
  if (!image) return;
  imageWidth.value = Math.min(100, Math.max(10, widthPercentOf(image) || 100));
  imageAlt.value = image.getAttribute('alt') ?? '';
  imageDialogOpen.value = true;
}

function applyImageDialog(): void {
  const image = selectedImage.value;
  imageDialogOpen.value = false;
  if (!image) return;
  edit(() => {
    image.removeAttribute('width');
    image.removeAttribute('height');
    image.style.width = `${imageWidth.value}%`;
    image.style.maxWidth = '100%';
    image.style.height = 'auto';
    image.setAttribute('alt', imageAlt.value.trim());
  });
}

/* Clipboard ------------------------------------------------------------- */

function selectionHtml(): { html: string; text: string } | undefined {
  const selection = doc?.getSelection();
  if (!doc || !selection || selection.isCollapsed || !selection.rangeCount) return undefined;
  const holder = doc.createElement('div');
  holder.append(selection.getRangeAt(0).cloneContents());
  return { html: sanitizeHtml(holder.innerHTML, false), text: selection.toString() };
}

async function copySelection(cut: boolean): Promise<void> {
  const content = selectionHtml();
  if (!content) return;
  let copied = false;
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([content.html], { type: 'text/html' }),
        'text/plain': new Blob([content.text], { type: 'text/plain' }),
      }),
    ]);
    copied = true;
  } catch {
    copied = await navigator.clipboard
      ?.writeText?.(content.text)
      .then(() => true)
      .catch(() => false);
  }
  // Cutting removes the text only once it is really on the clipboard.
  if (!copied) {
    error.value = tr(
      'copy_blocked',
      'The browser did not allow copying. Use Ctrl/Cmd+C or Ctrl/Cmd+X.'
    );
    return;
  }
  if (cut) edit(() => exec(doc!, 'delete'));
}

async function pasteFromClipboard(): Promise<void> {
  try {
    const items = await navigator.clipboard.read();
    let html = '';
    let text = '';
    const images: File[] = [];
    for (const item of items) {
      if (item.types.includes('text/html')) html ||= await (await item.getType('text/html')).text();
      if (item.types.includes('text/plain'))
        text ||= await (await item.getType('text/plain')).text();
      for (const type of item.types.filter((t) => t.startsWith('image/'))) {
        const blob = await item.getType(type);
        images.push(new File([blob], `image.${type.slice(6)}`, { type }));
      }
    }
    if (html || text) paste(html, text);
    else if (images.length) insertImages(images);
  } catch {
    error.value = tr(
      'paste_blocked',
      'The browser does not allow reading the clipboard. Use Ctrl/Cmd+V.'
    );
  }
}

function paste(html: string, text: string, mode: NxRichTextPasteMode = pasteMode.value): void {
  if (!doc) return;
  if (mode === 'ask' && html && isOfficeHtml(html)) {
    pendingPaste = { html, text };
    pasteOpen.value = true;
    return;
  }
  const content =
    mode === 'text' || !html
      ? textToHtml(text)
      : mode === 'destination'
        ? matchDestination(html)
        : sanitizeHtml(html, false);
  edit(() => insertHtmlAtCaret(doc!, allowedPictures(content)));
}

/**
 * Inserted markup keeps only pictures the field takes: none where pictures
 * are off, and embedded ones only as PNG, JPEG, GIF or WebP within the size
 * limit — the same rules as a picture file, whichever way it arrives.
 */
function allowedPictures(html: string): string {
  if (!/<img\b/i.test(html)) return html;
  const holder = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
  let rejected = false;
  for (const image of [...holder.querySelectorAll('img')]) {
    const src = image.getAttribute('src') ?? '';
    const data = /^data:([^;,]+)[^,]*,(.*)$/is.exec(src);
    const fits = data
      ? IMAGE_TYPES.includes(data[1].toLowerCase()) &&
        (data[2].length * 3) / 4 <= props.maxImageBytes
      : true;
    if (props.allowImages && fits) continue;
    rejected ||= props.allowImages;
    image.remove();
  }
  if (rejected)
    error.value = tr('image_rejected', 'Pictures must be PNG, JPEG, GIF or WebP and at most 8 MB.');
  return holder.innerHTML;
}

function choosePaste(mode: NxRichTextPasteMode): void {
  const pending = pendingPaste;
  pendingPaste = undefined;
  pasteOpen.value = false;
  if (pending) paste(pending.html, pending.text, mode);
}

function cancelPaste(): void {
  pendingPaste = undefined;
  pasteOpen.value = false;
  focusFrame();
}

function onPaste(event: ClipboardEvent): void {
  const data = event.clipboardData;
  if (!data || props.disabled) return;
  event.preventDefault();
  const html = data.getData('text/html');
  const text = data.getData('text/plain');
  const files = [...data.files];
  const images = files.filter((file) => file.type.startsWith('image/'));
  const meaningful =
    text.trim() || (html && !/^\s*(?:<[^>]*>\s*)*<img\b[^>]*>(?:\s*<[^>]*>)*\s*$/i.test(html));
  if (meaningful) paste(html, text);
  else if (images.length && props.allowImages) insertImages(images);
  // A picture that comes only as HTML (copied from a page) is pasted as HTML.
  else if (html && props.allowImages) paste(html, text, 'source');
  const others = files.filter((file) => !file.type.startsWith('image/') || !props.allowImages);
  if (others.length) emit('files', others);
}

/* Pictures --------------------------------------------------------------- */

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

function chooseImages(): void {
  if (!props.allowImages) return;
  choosingFile = true;
  try {
    fileInput.value?.click();
  } finally {
    choosingFile = false;
  }
}

function guardFileInput(event: MouseEvent): void {
  // A <label> around the field activates its first form control — this one.
  if (!choosingFile) event.preventDefault();
}

function onFileInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  const files = [...(input.files ?? [])];
  input.value = '';
  if (files.length) insertImages(files);
}

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function insertImages(files: File[]): void {
  if (!props.allowImages || !doc) return;
  const rejected = files.find(
    (file) => !IMAGE_TYPES.includes(file.type) || file.size > props.maxImageBytes
  );
  if (rejected) {
    error.value = tr('image_rejected', 'Pictures must be PNG, JPEG, GIF or WebP and at most 8 MB.');
    return;
  }
  error.value = '';
  const target = doc;
  const ticket = generation;
  const work: Promise<void> = Promise.all(files.map(readDataUrl))
    .then((urls) => {
      // Another text was loaded meanwhile: the pictures belonged to the old one.
      if (doc !== target || generation !== ticket) return;
      const html = urls.map((url) => `<img src="${url}" alt="" style="max-width: 100%;">`).join('');
      edit(() => insertHtmlAtCaret(target, html));
    })
    .catch(() => {
      error.value = tr(
        'image_rejected',
        'Pictures must be PNG, JPEG, GIF or WebP and at most 8 MB.'
      );
    })
    .finally(() => {
      pendingImages.delete(work);
    });
  pendingImages.add(work);
}

function onDrop(event: DragEvent): void {
  const files = [...(event.dataTransfer?.files ?? [])];
  if (!files.length || props.disabled) return;
  event.preventDefault();
  const images = props.allowImages ? files.filter((file) => IMAGE_TYPES.includes(file.type)) : [];
  if (images.length) {
    const point = doc?.caretPositionFromPoint?.(event.clientX, event.clientY);
    if (point && doc) doc.getSelection()?.collapse(point.offsetNode, point.offset);
    insertImages(images);
  }
  const others = files.filter((file) => !images.includes(file));
  if (others.length) emit('files', others);
}

function onDragOver(event: DragEvent): void {
  if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
}

/* Find and replace ------------------------------------------------------- */

const findOpen = ref(false);
const findQuery = ref('');
const findReplacement = ref('');
const findMatchCase = ref(false);
const findWholeWord = ref(false);
const findReplaceOpen = ref(false);
const findIndex = ref<number | null>(null);
const findMatches = shallowRef<Range[]>([]);
const HIGHLIGHT = 'nx-html-editor-find';

function openFind(replace = false): void {
  findReplaceOpen.value = replace;
  findOpen.value = true;
  const selected = doc?.getSelection()?.toString();
  if (selected && !selected.includes('\n')) findQuery.value = selected;
  searchText();
}

function closeFind(): void {
  findOpen.value = false;
  findMatches.value = [];
  findIndex.value = null;
  highlight([]);
  focusFrame();
}

function highlight(ranges: Range[]): void {
  const view = doc?.defaultView as
    | (Window & { CSS: typeof CSS; Highlight?: typeof Highlight })
    | null
    | undefined;
  if (!view?.Highlight || !view.CSS?.highlights) return;
  if (ranges.length) view.CSS.highlights.set(HIGHLIGHT, new view.Highlight(...ranges));
  else view.CSS.highlights.delete(HIGHLIGHT);
}

function searchText(keepIndex?: number): void {
  const el = body();
  const query = findQuery.value;
  if (!doc || !el || !query) {
    findMatches.value = [];
    findIndex.value = null;
    highlight([]);
    return;
  }
  // Matched on the text itself: offsets of a lower-cased copy can shift (İ → i̇).
  const pattern = new RegExp(
    query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    findMatchCase.value ? 'gu' : 'giu'
  );
  const ranges: Range[] = [];
  const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const isWord = (ch: string | undefined) => !!ch && /[\p{L}\p{N}_]/u.test(ch);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const value = node.textContent ?? '';
    for (const match of value.matchAll(pattern)) {
      const at = match.index ?? 0;
      const end = at + match[0].length;
      if (!match[0].length) break;
      if (findWholeWord.value && (isWord(value[at - 1]) || isWord(value[end]))) continue;
      const range = doc.createRange();
      range.setStart(node, at);
      range.setEnd(node, end);
      ranges.push(range);
    }
  }
  findMatches.value = ranges;
  findIndex.value = ranges.length ? Math.min(keepIndex ?? 0, ranges.length - 1) : null;
  highlight(ranges);
  if (keepIndex === undefined) showMatch();
}

/** After an edit the stored matches point at old text: search again, staying put. */
function refreshFind(): void {
  if (findOpen.value) searchText(findIndex.value ?? 0);
}

function showMatch(): void {
  const range = findIndex.value == null ? undefined : findMatches.value[findIndex.value];
  if (!range || !doc) return;
  const selection = doc.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range.cloneRange());
  range.startContainer.parentElement?.scrollIntoView({ block: 'nearest' });
}

function stepMatch(direction: 1 | -1): void {
  const total = findMatches.value.length;
  if (!total) return;
  findIndex.value = ((findIndex.value ?? -1) + direction + total) % total;
  showMatch();
}

function replaceMatch(): void {
  searchText(findIndex.value ?? 0);
  const range = findIndex.value == null ? undefined : findMatches.value[findIndex.value];
  if (!range || !doc) return;
  const d = doc;
  edit(() => {
    const selection = d.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    exec(d, 'insertText', findReplacement.value);
  });
  showMatch();
}

function replaceAll(): void {
  searchText(findIndex.value ?? 0);
  const ranges = findMatches.value;
  if (!ranges.length || !doc) return;
  const d = doc;
  edit(() => {
    for (const range of [...ranges].reverse()) {
      range.deleteContents();
      range.insertNode(d.createTextNode(findReplacement.value));
    }
    d.body.normalize();
  });
  showMatch();
}

watch([findQuery, findMatchCase, findWholeWord], () => {
  if (findOpen.value) searchText();
});

/* Keyboard --------------------------------------------------------------- */

/** Keys the browser edits with natively; never handed on to the host. */
const NATIVE_EDITING = new Set(['a', 'c', 'v', 'x']);
/** A tab character that keeps its width: `pre` stops the text from folding it into a space. */
/** `Apple-tab-span` makes WebKit type after the span, not into it (it would never wrap). */
const TAB_HTML = '<span class="Apple-tab-span" style="white-space:pre">\t</span>';

/**
 * Hands a key on to the page around the frame: a host's own shortcuts (F5
 * snippets, Ctrl+Enter send, Escape) see it as if it had happened there.
 */
function forwardKey(event: KeyboardEvent): void {
  const target = frame.value;
  if (!target) return;
  const forwarded = new KeyboardEvent(event.type, {
    key: event.key,
    code: event.code,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    shiftKey: event.shiftKey,
    altKey: event.altKey,
    repeat: event.repeat,
    bubbles: true,
    cancelable: true,
  });
  target.dispatchEvent(forwarded);
  if (forwarded.defaultPrevented) event.preventDefault();
}

/**
 * Reports a mouse press to the page around the frame as `nx-editor-press`
 * with its click count: no pointer event leaves the frame, and a field around
 * the editor opens its full editor on a double click (BLP/nx#2818). A host
 * that cancels the report cancels the press (no word selection under it).
 */
function forwardPress(event: MouseEvent): void {
  const target = frame.value;
  if (!target || event.button !== 0) return;
  const press = new CustomEvent('nx-editor-press', {
    bubbles: true,
    cancelable: true,
    detail: { clickCount: event.detail },
  });
  target.dispatchEvent(press);
  if (press.defaultPrevented) event.preventDefault();
}

function onKeydown(event: KeyboardEvent): void {
  if (event.isComposing || event.keyCode === 229 || !doc) return;
  const mod = event.ctrlKey || event.metaKey;
  const key = event.key.toLowerCase();
  const handle = (run: () => void) => {
    event.preventDefault();
    run();
  };
  if (mod && !event.altKey) {
    if (key === 'z' && !event.shiftKey) return handle(undo);
    if ((key === 'z' && event.shiftKey) || (key === 'y' && !event.shiftKey)) return handle(redo);
    if (key === 's' && !event.shiftKey) return handle(() => void requestSave());
    if (key === 'k') return handle(() => toolbar.value?.openLink());
    if (key === 'f') return handle(() => openFind(false));
    if (key === 'h') return handle(() => openFind(true));
    if (key === 'm')
      return handle(() => onCommand({ id: 'indent', delta: event.shiftKey ? -1 : 1 }));
    const marks: Record<string, 'bold' | 'italic' | 'underline'> = {
      b: 'bold',
      i: 'italic',
      u: 'underline',
    };
    if (marks[key] && !event.shiftKey)
      return handle(() => onCommand({ id: 'mark', mark: marks[key] }));
    const aligns: Record<string, 'left' | 'center' | 'right' | 'justify'> = {
      l: 'left',
      e: 'center',
      r: 'right',
      j: 'justify',
    };
    if (aligns[key] && !event.shiftKey)
      return handle(() => onCommand({ id: 'align', align: aligns[key] }));
    if (event.shiftKey && (event.key === '>' || event.key === '.'))
      return handle(() => stepFontSize(1));
    if (event.shiftKey && (event.key === '<' || event.key === ','))
      return handle(() => stepFontSize(-1));
    if (NATIVE_EDITING.has(key) && !event.shiftKey) return;
  }
  if (event.key === 'Tab' && !mod && !event.altKey) {
    if (closestIn(doc, 'td, th')) {
      const d = doc;
      if (moveToCell(d, event.shiftKey ? -1 : 1)) return handle(() => refreshState());
      if (!event.shiftKey) return handle(() => edit(() => tableEdit(d, 'row', 'insert')));
    }
    if (closestIn(doc, 'li'))
      return handle(() => onCommand({ id: 'indent', delta: event.shiftKey ? -1 : 1 }));
    // Shift+Tab stays the native move out of the text (BLP/nx#3328).
    if (tabCharacter.value && !event.shiftKey) {
      const d = doc;
      return handle(() =>
        edit(() => {
          leaveTabSpan(d);
          insertHtmlAtCaret(d, TAB_HTML);
        })
      );
    }
    return;
  }
  if (event.key === 'Escape' && findOpen.value) return handle(closeFind);
  // An extension's shortcut (F5: text blocks) opens its panel instead of
  // reaching the page around the frame.
  const extension = richTextExtensionForKey(extensions.value, event);
  if (extension && toolbar.value?.openExtension(extension.id)) return handle(() => {});
  if (mod || event.altKey || /^F\d{1,2}$/.test(event.key) || event.key === 'Escape')
    forwardKey(event);
}

function onBeforeInput(event: InputEvent): void {
  if (event.inputType === 'historyUndo') {
    event.preventDefault();
    undo();
  } else if (event.inputType === 'historyRedo') {
    event.preventDefault();
    redo();
  }
}

function onInput(): void {
  if (inCommand) return;
  changed(true);
}

function onClick(event: MouseEvent): void {
  const target = event.target as Element | null;
  const image = target?.closest('img');
  selectImage(image && body()?.contains(image) ? (image as HTMLImageElement) : null);
  const link = target?.closest('a[href]');
  if (link && (event.ctrlKey || event.metaKey)) {
    event.preventDefault();
    window.open((link as HTMLAnchorElement).href, '_blank', 'noopener,noreferrer');
  }
}

/* Frame lifecycle -------------------------------------------------------- */

function onFrameLoad(): void {
  const next = frame.value?.contentDocument;
  if (!next || next === doc) return;
  doc = next;
  next.addEventListener('keydown', onKeydown);
  next.addEventListener('beforeinput', onBeforeInput);
  next.addEventListener('input', onInput);
  next.addEventListener('selectionchange', scheduleState);
  next.addEventListener('paste', onPaste);
  next.addEventListener('drop', onDrop);
  next.addEventListener('dragover', onDragOver);
  next.addEventListener('click', onClick);
  next.addEventListener('mousedown', forwardPress);
  next.defaultView?.addEventListener('focus', onFrameFocus);
  next.defaultView?.addEventListener('blur', checkFocusLeft);
  next.addEventListener('mouseup', scheduleState);
  next.addEventListener('keyup', scheduleState);
  ready.value = true;
  mount(original);
  if (props.focusOnLoad) void focus();
}

/**
 * Focuses the editable body. An existing selection is kept; without one the
 * caret goes behind the last block (where an appended paragraph stays its
 * own). `atEnd` puts it at the end of the last text instead, always — a host
 * whose press landed beside the text (BLP/nx#3318).
 */
async function focus(options?: { atEnd?: boolean }): Promise<void> {
  await nextTick();
  await nextTick();
  if (!ready.value || props.disabled || !doc) return;
  focusFrame();
  if (options?.atEnd === true) {
    placeCaretAtTextEnd(doc);
    return;
  }
  const selection = doc.getSelection();
  if (!selection?.rangeCount || !doc.body.contains(selection.anchorNode)) placeCaretAtEnd(doc);
}

function onFocusEnter(): void {
  focusInside = true;
}

/**
 * The frame took the focus. Its document's events stay inside the frame, so
 * the hosts' `focusin` (NxEditorInput keeps an echo from overwriting the text
 * being typed) is raised on the editor itself.
 */
function onFrameFocus(): void {
  focusInside = true;
  root.value?.dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
}

/**
 * The focus left the frame or a toolbar control. A click on the toolbar moves
 * it out of the frame and back, and a window that loses focus keeps the frame
 * as the page's active element — only a focus that really went elsewhere on
 * the page commits, once.
 */
function checkFocusLeft(): void {
  setTimeout(() => {
    const active = document.activeElement;
    if (!focusInside || (active && root.value?.contains(active))) return;
    focusInside = false;
    const pending = prepareSave();
    if (pending) void pending.then(() => emit('focusout'));
    else emit('focusout');
  });
}

function onRootFocusout(event: FocusEvent): void {
  const next = event.relatedTarget;
  if (next instanceof Node && root.value?.contains(next)) return;
  checkFocusLeft();
}

function onRootKeydown(event: KeyboardEvent): void {
  if (
    event.isComposing ||
    event.keyCode === 229 ||
    !(event.ctrlKey || event.metaKey) ||
    event.altKey ||
    event.shiftKey
  )
    return;
  if (event.key.toLowerCase() !== 's') return;
  event.preventDefault();
  event.stopPropagation();
  void requestSave();
}

async function requestSave(): Promise<void> {
  if (props.disabled) return;
  await prepareSave();
  emit('save');
}

/** Waits for every picture still being read in, then publishes the current text. */
function prepareSave(): Promise<void> | undefined {
  if (!pendingImages.size) {
    publish();
    return undefined;
  }
  const drain = async (): Promise<void> => {
    while (pendingImages.size) await Promise.all([...pendingImages]);
    publish();
  };
  return drain();
}

function insertHTML(html: string): Promise<void> {
  if (props.disabled || !doc) return Promise.resolve();
  const clean = allowedPictures(sanitizeHtml(html, false));
  edit(() => insertHtmlAtCaret(doc!, clean));
  return Promise.resolve();
}

async function appendHTML(html: string): Promise<void> {
  if (props.disabled || !doc) return;
  const d = doc;
  focusFrame();
  placeCaretAtEnd(d);
  await insertHTML(html);
  await focus();
}

onMounted(() => {
  // A frame whose srcdoc was already parsed before Vue listened.
  if (frame.value?.contentDocument?.readyState === 'complete' && frame.value.contentDocument.body)
    onFrameLoad();
  // The theme sits on <html> (`data-theme`, the design's classes and variables);
  // a colour that fades in with a transition is read again when it is done.
  const page = root.value?.ownerDocument;
  if (page && typeof MutationObserver !== 'undefined') {
    lookObserver = new MutationObserver(followFieldLook);
    lookObserver.observe(page.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class', 'style'],
    });
  }
  schemeQuery = page?.defaultView?.matchMedia?.('(prefers-color-scheme: dark)');
  schemeQuery?.addEventListener?.('change', followFieldLook);
  root.value?.addEventListener('transitionend', followFieldLook);
});

onBeforeUnmount(() => {
  lookObserver?.disconnect();
  schemeQuery?.removeEventListener?.('change', followFieldLook);
  root.value?.removeEventListener('transitionend', followFieldLook);
  resizeObserver?.disconnect();
  clearTimeout(stateFrame);
  highlight([]);
});

watch(pasteMode, refreshState);

const statusText = computed(() => {
  switch (props.recoveryStatus) {
    case undefined:
    case '':
      return '';
    case 'saved':
      return tr('recovery_saved', 'Local backup saved');
    case 'saving':
      return tr('recovery_saving', 'Saving local backup…');
    case 'error':
    case 'conflict':
      return tr('recovery_unavailable', 'Local backup unavailable');
    default:
      return tr('recovery_ready', 'Local backup ready');
  }
});

defineExpose({
  getHTML,
  setHTML,
  insertHTML,
  appendHTML,
  prepareSave,
  focus,
  /** The editable body inside the frame (the frame itself until it has loaded). */
  inputElement: computed<HTMLElement | null>(() => bodyEl.value ?? frame.value),
  // The selection lives in the frame's document; hosts do not navigate it.
  getSelection: () => undefined,
});
</script>

<template>
  <div
    ref="root"
    class="nx-html-editor"
    :class="{ 'nx-html-editor--flow': !internalScroll, 'nx-html-editor--disabled': disabled }"
    @focusin="onFocusEnter"
    @focusout="onRootFocusout"
    @keydown="onRootKeydown"
  >
    <NxRichTextToolbar
      v-if="showActionBar && !disabled"
      ref="toolbar"
      v-model:link-open="linkOpen"
      v-model:table-open="tableOpen"
      v-model:link-href="linkHref"
      :state="state"
      :font-families="families"
      :font-sizes="fontSizes"
      :line-spacings="lineSpacings"
      :disabled-commands="disabledCommands"
      :allow-images="allowImages"
      :layout="toolbarLayout"
      :groups="toolbarGroups"
      :extra-actions="extraActions"
      :customizable="customizable"
      :extensions="extensions"
      :extension-context="extensionContext"
      @command="onCommand"
      @extra="(id: string) => emit('extra', id)"
      @customize="emit('customize')"
      @extension-closed="focus"
    >
      <template v-if="$slots['toolbar-end']" #end>
        <slot name="toolbar-end" />
      </template>
      <template v-if="$slots['insert-actions']" #insert-actions>
        <slot name="insert-actions" />
      </template>
    </NxRichTextToolbar>
    <div v-if="findOpen" class="nx-html-editor__find">
      <NxFindBar
        v-model:query="findQuery"
        v-model:replacement="findReplacement"
        v-model:match-case="findMatchCase"
        v-model:whole-word="findWholeWord"
        v-model:replace-open="findReplaceOpen"
        :current="findIndex"
        :total="findMatches.length"
        replaceable
        :find-label="tr('find', 'Find')"
        :replace-label="tr('replace', 'Replace')"
        @next="stepMatch(1)"
        @prev="stepMatch(-1)"
        @replace="replaceMatch"
        @replace-all="replaceAll"
        @close="closeFind"
      />
    </div>
    <iframe
      ref="frame"
      class="nx-html-editor__frame"
      sandbox="allow-same-origin"
      referrerpolicy="no-referrer"
      :srcdoc="SHELL"
      :title="label"
      @load="onFrameLoad"
    />
    <div v-if="error" class="nx-html-editor__error" role="alert">{{ error }}</div>
    <div v-if="showActionBar && !disabled" class="nx-html-editor__status">
      <slot name="status-start" />
      <span v-if="statusText" role="status">{{ statusText }}</span>
      <span>{{ words }} {{ tr('words', 'words') }}</span>
      <span v-if="tabCharacter">{{ tabHint }}</span>
      <span class="nx-html-editor__status-end">{{ tr('save_shortcut', 'Ctrl / ⌘ S · Save') }}</span>
    </div>
    <input
      ref="fileInput"
      class="nx-html-editor__file"
      type="file"
      accept="image/png,image/jpeg,image/gif,image/webp"
      multiple
      tabindex="-1"
      aria-hidden="true"
      @click="guardFileInput"
      @change="onFileInput"
    />
    <NxModal v-model:open="imageDialogOpen" :close-on-click-outside="false">
      <template #header>{{ tr('image_tools', 'Image tools') }}</template>
      <template #body>
        <div class="nx-html-editor__image-fields">
          <span>{{ tr('image_width', 'Width') }}</span>
          <NxSlider
            v-model="imageWidth"
            :min="10"
            :max="100"
            :step="5"
            show-value
            :format="(value: number) => `${value} %`"
            :aria-label="tr('image_width', 'Width')"
          />
          <span>{{ tr('image_description', 'Description (alternative text)') }}</span>
          <NxTextInput
            v-model="imageAlt"
            :aria-label="tr('image_description', 'Description (alternative text)')"
          />
        </div>
      </template>
      <template #footer>
        <NxButton @click="applyImageDialog">{{ tr('apply', 'Apply') }}</NxButton>
        <NxButton variant="ghost" @click="imageDialogOpen = false">
          {{ tr('cancel', 'Cancel') }}
        </NxButton>
      </template>
    </NxModal>
    <NxModal v-model:open="pasteOpen" :close-on-click-outside="false">
      <template #header>{{ tr('paste_title', 'Paste from Word / Outlook') }}</template>
      <template #body>
        <p>{{ tr('paste_help', 'Choose the formatting before inserting the copied text.') }}</p>
      </template>
      <template #footer>
        <NxButton @click="choosePaste('source')">
          {{ tr('paste_source', 'Keep source formatting') }}
        </NxButton>
        <NxButton variant="outline" @click="choosePaste('destination')">
          {{ tr('paste_destination', 'Match destination') }}
        </NxButton>
        <NxButton variant="outline" @click="choosePaste('text')">
          {{ tr('paste_text', 'Plain text') }}
        </NxButton>
        <NxButton variant="ghost" @click="cancelPaste">{{ tr('cancel', 'Cancel') }}</NxButton>
      </template>
    </NxModal>
  </div>
</template>

<style scoped>
.nx-html-editor {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  height: 100%;
  min-height: calc(var(--nxt-area-size-md) * 2);
  overflow: hidden;
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
}

.nx-html-editor--flow {
  height: auto;
  min-height: 0;
  overflow: visible;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.nx-html-editor__frame {
  flex: 1;
  width: 100%;
  min-height: 0;
  border: 0;
  background: var(--nxt-surface-bg);
}

.nx-html-editor--flow .nx-html-editor__frame {
  flex: none;
  min-height: var(--nxt-control-size-md);
  background: transparent;
}

.nx-html-editor__find {
  display: flex;
  justify-content: flex-end;
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-html-editor__error,
.nx-html-editor__status {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-xs) var(--nxt-space-md);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-html-editor__error {
  color: var(--nxt-danger-bg);
}

/* Quiet: no rule above it, the host's bits and the counts left, the save key right. */
.nx-html-editor__status {
  flex-wrap: wrap;
  row-gap: var(--nxt-space-2xs);
  padding-block: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-top: 0;
}

.nx-html-editor__status-end {
  margin-left: auto;
}

.nx-html-editor__file {
  display: none;
}

.nx-html-editor__image-fields {
  display: grid;
  grid-template-columns: max-content minmax(calc(var(--nxt-size-unit) * 15), 1fr);
  align-items: center;
  gap: var(--nxt-space-sm) var(--nxt-space-md);
}
</style>
